import React, { useState, useEffect } from 'react';
import { fetchShippingInsurance } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

const DASH = String.fromCharCode(8212);
const ARROW_L = String.fromCharCode(8592);
const ARROW_R = String.fromCharCode(8594);
const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}
const money = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = n => Number(n || 0).toLocaleString('en-US');

export default function ShippingInsurance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchShippingInsurance({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const H = data?.header ?? {};
  const rows = data?.records?.data ?? (Array.isArray(data) ? data : data?.data ?? []);
  const total = data?.records?.totalRows ?? data?.totalRows ?? rows.length;

  const groups = [
    { title: 'Orders', cards: [
      { label: 'Total Orders', value: num(H.totalOrders) },
      { label: 'New', value: num(H.newOrders) },
      { label: 'Reship', value: num(H.reshipOrders) },
      { label: 'Packed', value: num(H.packedOrders) },
      { label: 'Remaining', value: num(H.remainingOrders), warn: Number(H.remainingOrders) > 0 },
      { label: 'To Print', value: num(H.ordersToPrint), warn: Number(H.ordersToPrint) > 0 },
    ]},
    { title: 'Financial', cards: [
      { label: 'Total Sales', value: money(H.totalSales) },
      { label: 'Total Profit', value: money(H.totalProfit), accent: true },
      { label: 'Avg Profit / Packed', value: money(H.averageProfit) },
      { label: 'Insurance Cost', value: money(H.totalInsuranceCostAmount) },
      { label: 'Claim Amount', value: money(H.totalClaimAmount) },
    ]},
    { title: 'Packages', cards: [
      { label: 'UPS', value: num(H.upsPackages) },
      { label: 'FedEx', value: num(H.fedexPackages) },
      { label: 'Other', value: num(H.otherPackages) },
    ]},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Shipping Insurance Report</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Completed orders shipped in the range (by ship date)</div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Ship Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {!loading && !error && (H.totalOrders != null) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {groups.map(g => (
            <div key={g.title}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5 }}>{g.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {g.cards.map(({ label, value, accent, warn }) => (
                  <div key={label} className="card" style={{ padding: '9px 12px' }}>
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: warn ? 'var(--red)' : accent ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Shipped Orders {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No records found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship To</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Location</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Carrier</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Order Total</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Profit</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Insurance</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Claim</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.orderId ?? i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? DASH}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.shipTo ?? DASH)}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{[row.city ? toTitleCase(row.city) : null, row.state].filter(Boolean).join(', ') || DASH}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{row.shipType ?? DASH}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right' }}>{money(row.orderTotal)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: Number(row.profit) < 0 ? 'var(--red)' : 'var(--gray-700)' }}>{money(row.profit)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: Number(row.insuranceCost) > 0 ? 'var(--orange)' : 'var(--gray-400)' }}>{Number(row.insuranceCost) > 0 ? money(row.insuranceCost) : DASH}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        {row.isClaim
                          ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#fef3f0', color: '#c0392b' }}>Claim</span>
                          : <span style={{ color: 'var(--gray-300)' }}>{DASH}</span>}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {row.shipDate ? new Date(row.shipDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : DASH}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>{ARROW_L} Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next {ARROW_R}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
